import EventEmitter from "./EventEmitter";
import { assign, escapeRegExp, throttle } from "./common";
import FileModel, { FileStates } from "./FileModel";
import ImageModel from "./ImageModel";
import InvalidFileModel from "./InvalidFileModel";

/**
 * Converts string expressions like `  image/*   ,     text/svg` to RegExp
 * @param {string} expr
 * @returns {RegExp}
 */
function createMimeRegExp(expr) {
  const str = "^(?:" + expr
    .split(",")
    .map(s => s.trim().replace(/\*/g, "ALL"))
    .map(escapeRegExp)
    .map(s => s.replace(/ALL/g, ".*"))
    .join("|") + ")$";

  return new RegExp(str, "i");
}


function maybeResize(file, opts, cb) {
  if (!window.File) {
    cb(file);
    return;
  }
  if (!(file instanceof File)) {
    cb(file);
    return;
  }
  if (!(/^image\//.test(file.type))) {
    cb(file);
    return;
  }

  const max_size = opts.resize_to;

  if (!max_size) {
    cb(file);
    return;
  }

  try {
    const reader = new FileReader();

    reader.onload = function (readerEvent) {
      const image = new Image();

      image.onload = function () {
        try {
          const canvas = document.createElement('canvas');
          let width = image.width;
          let height = image.height;
          if (width > height) {
            if (width > max_size) {
              height *= max_size / width;
              width = max_size;
            }
          } else {
            if (height > max_size) {
              width *= max_size / height;
              height = max_size;
            }
          }
          canvas.width = width;
          canvas.height = height;
          canvas.getContext('2d').drawImage(image, 0, 0, width, height);
          canvas.toBlob(blob => {
            try {
              const newFile = new File([blob], file.name, {type: 'image/jpeg'});
              cb(newFile);
            } catch (e) {
              console.warn(e);
              cb(file);
              return;
            }
          }, 'image/jpeg');
        } catch (e) {
          console.warn(e);
          cb(file);
          return;
        }
      }

      image.src = readerEvent.target.result;
    }

    reader.readAsDataURL(file);
  } catch(e) {
    console.warn(e);
    cb(file);
    return;
  }
}

const UploaderModel = EventEmitter.extend(
  /** @lends UploaderModel.prototype */
  {
    /**
     * Model class for the file list
     * @param {object} opts
     * @constructs
     * @extends EventEmitter
     */
    constructor(opts) {
      EventEmitter.call(this);

      /**
       * @protected
       * @type {object}
       */
      this.opts = assign({}, this.getDefaultOptions(), opts);

      /**
       * List of the files
       * @type {FileModel[]}
       */
      this.items = [];

      /**
       * Backup of {@link UploaderModel#items}
       * @type {FileModel[]}
       */
      this.lastItems = []; // TODO: needs testing

      this.mimeRegExp = createMimeRegExp(this.opts.mimes);
      this.sortDirty = true;
    },

    setUrlBase(base) {
      this.opts.adapter.opts.url.base = base;
    },

    /**
     * Main method for adding files.
     * Add files, typically from file input od drag'n'drop dataTransfer,
     * but also from HTML ("static" files, see {@link FileModel}).
     * @param {File[]|array} fileList
     * @param {boolean} [force=false] - force adding even if limit is exceeded
     */
    addFiles(fileList, force = false) {
      const cnt = force ? fileList.length : Math.min(fileList.length, this.opts.max - this.items.length);

      const emitThrottled = throttle(() => { this.emit("files_changed"); });

      for (let i = 0; i < cnt; i++) {
        maybeResize(fileList[i], this.opts, file => {
          const model = this.initItemModel(file);
          model.upload();
          this.lastItems = this.items = [...this.items, model];
          emitThrottled();
        });
      }
    },

    /**
     * Tell if file is of a supported type
     * @param {File} file
     * @returns {boolean}
     * @protected
     */
    fileSupported(file) {
      return this.mimeRegExp.test(file.type);
    },

    /**
     * Test if file count limit is reached
     * @returns {boolean}
     */
    isFull() {
      return this.items.length >= this.opts.max;
    },

    /**
     * Factory method for item model representing given file
     * @param {File} file
     * @returns {FileModel}
     * @protected
     */
    createItemModel(file) {
      if(file instanceof File && !this.fileSupported(file)) {
        return new InvalidFileModel(file, this.opts);
      }

      if("url" in file || /^image\//.test(file.type)) return new ImageModel(file, this.opts);
      return new FileModel(file, this.opts);
    },

    /**
     * Create an item model representing given file and bind necessary listeners on it.
     * @param {File|*} file
     * @returns {FileModel}
     */
    initItemModel(file) {
      const model = this.createItemModel(file);

      this.listenTo(model, "all", evt => { this.emit(`file:${evt.type}`, evt); });
      this.listenTo(model, "upload_done", ({ target }) => { this.handleItemUploadDone(target); });

      this.listenTo(model, "state_changed", ({ state, target }) => {
        switch(state) {
          case FileStates.DELETED:
            this.handleItemDeleted(target);
            break;
        }
      });

      return model;
    },

    /**
     * Handle deletion of a file
     * @param {FileModel} model
     * @protected
     */
    handleItemDeleted(model) {
      const idx = this.items.indexOf(model);
      if(idx === -1) return;
      this.items.splice(idx, 1);
      this.emit("files_changed");
    },

    /**
     * Handle single file upload completion
     * @param {FileModel} model
     * @protected
     */
    handleItemUploadDone(model) {
      for(let i = 0; i < this.items.length; i++) {
        if(this.items[i].state === FileStates.UPLOADING) return;
      }

      // all uploaded, tell server how to sort it
      // in next tick so other listeners come first (e.g. to alter adapter opts)
      setTimeout(() => this.uploadSort(), 0);
    },

    /**
     * Reorders items, so that `model` gets to the same position, as `ref` was
     * @param {FileModel} model
     * @param {FileModel} ref
     */
    insertBefore(model, ref) {
      const idxRef = this.items.indexOf(ref);
      if(idxRef === -1) return;
      const idx = this.items.indexOf(model);
      if(idx === -1) return;
      this.items.splice(idx, 1);
      this.items.splice(idxRef, 0, model);
      this.emit("files_changed");
      this.sortDirty = true;
    },

    /**
     * Callback for UploadAdapter sort action
     * @param {string} err
     * @protected
     */
    handleSortDone(err) {
      if(err) {
        // TODO: warn
        console.error("Uploader: sorting error", err);
        this.items = this.lastItems;
        this.emit("files_changed");
      } else {
        this.lastItems = this.items;
        this.sortDirty = false;
      }
    },

    /**
     * Upload the current order of items
     */
    uploadSort() {
      if(!this.sortDirty) return;
      this.opts.adapter.sortFiles(this.items, err => { this.handleSortDone(err); });
    },

    /**
     * Factory method for default options
     * @returns {object}
     * @protected
     */
    getDefaultOptions() {
      return {
        max: Infinity
      };
    }
  });

export default UploaderModel;